import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { getStore, getUser, clearUser, clearStore, getUsageToday, getChalannEnabled, setChalannEnabled, isBarLikeGiro, isOnePay } from "@/lib/api";
import { ArrowLeft, LogOut, Store as StoreIcon, User, ShieldCheck, Sparkles, Power, PowerOff, KeyRound, ScanBarcode } from "lucide-react";
import { toast } from "sonner";
import PinSetupModal from "@/components/chalann/PinSetupModal";

export default function DashNav() {
  const store = getStore();
  const user = getUser();
  const navigate = useNavigate();
  const location = useLocation();
  const showBack = location.pathname !== "/dashboard";
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/dashboard");
  };
  const [usage, setUsage] = useState(null);
  const [chalannOn, setChalannOn] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const onePay = isOnePay();

  useEffect(() => {
    if (!store?.id) return;
    let mounted = true;
    const load = () => (onePay ? Promise.resolve() : getUsageToday().then((u) => { if (mounted) setUsage(u); }).catch(() => {}));
    const loadChalann = () => getChalannEnabled().then((r) => { if (mounted) setChalannOn(!!r.enabled); }).catch(() => {});
    load(); loadChalann();
    const iv = setInterval(load, 15000);
    const onFocus = () => { load(); loadChalann(); };
    window.addEventListener("focus", onFocus);
    window.addEventListener("chalann:voice-used", load);
    window.addEventListener("chalann:toggle-changed", loadChalann);
    return () => { mounted = false; clearInterval(iv); window.removeEventListener("focus", onFocus); window.removeEventListener("chalann:voice-used", load); window.removeEventListener("chalann:toggle-changed", loadChalann); };
  }, [store?.id, onePay]);

  const flipChalann = async () => {
    if (toggling) return;
    setToggling(true);
    const next = !chalannOn;
    try {
      await setChalannEnabled(next);
      setChalannOn(next);
      window.dispatchEvent(new CustomEvent("chalann:toggle-changed"));
      toast.success(next ? "Chalán encendido · listo para escucharte" : "Chalán apagado · la voz queda en pausa");
    } catch { toast.error("No se pudo cambiar el estado de Chalán"); }
    finally { setToggling(false); }
  };

  const changeStore = () => {
    clearStore();
    navigate("/setup");
  };
  const switchUser = () => {
    clearUser();
    navigate("/login");
  };

  return (
    <header className="sticky top-0 z-40 border-b border-white/10 bg-[#050505]/85 backdrop-blur-xl" data-testid="dash-nav">
      <div className="max-w-[1600px] mx-auto px-4 md:px-10 py-3 md:py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-4">
        {/* Row 1 (mobile): Logo + actions inline. Desktop: just logo. */}
        <div className="flex items-center justify-between md:justify-start gap-2 md:gap-3 min-w-0">
          <div className="flex items-center gap-2 md:gap-3 min-w-0">
            {showBack && (
              <button
                onClick={goBack}
                className="flex items-center justify-center w-9 h-9 rounded-full border border-white/10 bg-white/[0.03] text-white/70 hover:text-white hover:border-white/30 hover:bg-white/[0.08] transition flex-shrink-0"
                title="Regresar"
                aria-label="Regresar"
                data-testid="dash-back-btn"
              >
                <ArrowLeft size={16} />
              </button>
            )}
            <NavLink to="/dashboard" className="flex items-center gap-2 group min-w-0" data-testid="dash-logo">
              <span className="w-2 h-2 rounded-full bg-[#ff5a00] group-hover:bg-[#ccff00] transition-colors duration-300 flex-shrink-0" />
              <span className="font-display text-lg tracking-tighter">CHALANN</span>
              <span className="hidden md:inline text-white/30 mx-2">/</span>
              <span className="hidden md:inline font-mono text-[11px] tracking-widest text-white/50 uppercase truncate">
                {store?.name || "sin tienda"}
              </span>
            </NavLink>
          </div>

          {/* Mobile-only compact right actions (visible next to logo) */}
          <div className="flex md:hidden items-center gap-2 flex-shrink-0">
            {onePay ? (
              <span className="flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-[#CCFF00]/40 bg-[#CCFF00]/10 text-[#CCFF00] font-mono text-[9px] tracking-widest uppercase" data-testid="onepay-badge-mobile"><ScanBarcode size={11} /> ONE PAY</span>
            ) : (
            <button
              onClick={flipChalann}
              disabled={toggling}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full border transition font-mono text-[9px] tracking-widest uppercase disabled:opacity-50 ${
                chalannOn
                  ? "border-[#CCFF00]/50 bg-[#CCFF00]/10 text-[#CCFF00]"
                  : "border-white/15 bg-white/[0.03] text-white/50"
              }`}
              aria-pressed={chalannOn}
              title={chalannOn ? "Chalán encendido" : "Chalán apagado"}
              data-testid="chalann-toggle-btn-mobile"
            >
              {chalannOn ? <Power size={11} /> : <PowerOff size={11} />}
              <span>{chalannOn ? "ON" : "OFF"}</span>
              <span className={`w-1.5 h-1.5 rounded-full ${chalannOn ? "bg-[#CCFF00] animate-pulse" : "bg-white/25"}`} />
            </button>
            )}
            {user && !user.pin_set && (
              <button
                onClick={() => setPinModalOpen(true)}
                className="flex items-center justify-center w-8 h-8 rounded-full border border-[#FF5A00]/40 bg-[#FF5A00]/10 text-[#FF5A00] animate-pulse"
                title="Crea tu contraseña"
                data-testid="dash-pin-btn-mobile"
              >
                <KeyRound size={13} />
              </button>
            )}
            <button
              onClick={switchUser}
              className="flex items-center justify-center w-8 h-8 rounded-full border border-white/10 text-white/60 hover:text-white hover:border-white/30 transition"
              title={user ? `${user.name} · cambiar usuario` : "Iniciar sesión"}
              data-testid="dash-user-mobile"
            >
              {user?.role === "owner" ? <ShieldCheck size={13} className="text-[#ff5a00]" /> : <User size={13} />}
            </button>
          </div>
        </div>

        {/* Nav — scrollable horizontally on mobile so nothing gets cut off */}
        <nav
          className="flex items-center gap-1 md:gap-2 text-sm overflow-x-auto md:overflow-visible whitespace-nowrap -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide"
          data-testid="dash-nav-scroll"
        >
          {[
            { to: "/dashboard", label: "Inicio", tid: "nav-inicio" },
            { to: "/cobrar", label: "Cobrar", tid: "nav-cobrar" },
            ...(isBarLikeGiro(store?.giro) ? [{ to: "/mesas", label: "Mesas", tid: "nav-mesas" }] : []),
            { to: "/inventario", label: "Inventario", tid: "nav-inventario" },
            ...(user?.role === "owner" ? [{ to: "/precios-express", label: "Precios", tid: "nav-precios-express" }] : []),
            { to: "/fiados", label: "Fiados", tid: "nav-fiados" },
            { to: "/reporte", label: "Corte", tid: "nav-reporte" },
            ...(user?.role === "owner" ? [{ to: "/domicilio", label: "Domicilio", tid: "nav-domicilio" }] : []),
            ...(user?.role === "owner" ? [{ to: "/pagar", label: "Plan", tid: "nav-pagar" }] : []),
            { to: "/guia", label: "Guía", tid: "nav-guia" },
          ].map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.tid}
              className={({ isActive }) =>
                `px-3 md:px-4 py-2 rounded-full transition-colors duration-200 flex-shrink-0 ${
                  isActive ? "bg-white text-black" : "text-white/70 hover:text-white hover:bg-white/5"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* Desktop-only right actions */}
        <div className="hidden md:flex items-center gap-2">
          {onePay ? (
            <span className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#CCFF00]/40 bg-[#CCFF00]/10 text-[#CCFF00] font-mono text-[10px] tracking-widest uppercase" title="Plan ONE PAY · sin IA, con pistola de escaneo" data-testid="onepay-badge"><ScanBarcode size={12} /> ONE PAY · Escáner</span>
          ) : (
          <button
            onClick={flipChalann}
            disabled={toggling}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full border transition font-mono text-[10px] tracking-widest uppercase disabled:opacity-50 ${
              chalannOn
                ? "border-[#CCFF00]/50 bg-[#CCFF00]/10 text-[#CCFF00] hover:bg-[#CCFF00]/15"
                : "border-white/15 bg-white/[0.03] text-white/50 hover:text-white"
            }`}
            title={chalannOn ? "Toca para apagar Chalán" : "Toca para encender Chalán"}
            data-testid="chalann-toggle-btn"
            aria-pressed={chalannOn}
          >
            {chalannOn ? <Power size={12} /> : <PowerOff size={12} />}
            <span className="hidden sm:inline">
              {chalannOn ? "Chalán Encendido" : "Encender Chalán"}
            </span>
            <span className="sm:hidden">{chalannOn ? "ON" : "OFF"}</span>
            <span className={`w-2 h-2 rounded-full ${chalannOn ? "bg-[#CCFF00] animate-pulse" : "bg-white/25"}`} />
          </button>
          )}
          {usage && !onePay && (
            <div
              className={`hidden md:flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-[10px] font-mono tracking-widest uppercase ${
                usage.capped
                  ? "border-[#ff5a00]/50 bg-[#ff5a00]/10 text-[#ff5a00]"
                  : usage.remaining <= 50
                    ? "border-[#ccff00]/40 bg-[#ccff00]/5 text-[#ccff00]"
                    : "border-white/10 text-white/50"
              }`}
              title={usage.capped ? "Alcanzaste el cap diario · seguimos usando match local (0 tokens)" : `${usage.remaining} comandos IA disponibles hoy`}
              data-testid="usage-badge"
            >
              <Sparkles size={10} />
              <span data-testid="usage-count">{usage.llm_calls_today}/{usage.daily_cap}</span>
              <span className="text-white/30">IA</span>
            </div>
          )}
          {user && (
            <button
              onClick={() => setPinModalOpen(true)}
              className={`hidden md:flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-[10px] font-mono uppercase tracking-widest transition ${
                user.pin_set
                  ? "border-white/10 text-white/50 hover:text-white hover:border-white/30"
                  : "border-[#FF5A00]/40 bg-[#FF5A00]/10 text-[#FF5A00] hover:bg-[#FF5A00]/20 animate-pulse"
              }`}
              title={user.pin_set ? "Cambiar contraseña" : "Crea tu contraseña (opcional)"}
              data-testid="dash-pin-btn"
            >
              <KeyRound size={11} />
              <span>{user.pin_set ? "Contraseña" : "Crea PIN"}</span>
            </button>
          )}
          {user && (
            <button onClick={switchUser} className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 hover:border-white/30 text-xs" data-testid="dash-user">
              {user.role === "owner" ? <ShieldCheck size={12} className="text-[#ff5a00]" /> : <User size={12} className="text-[#ccff00]" />}
              <span className="text-white/80">{user.name}</span>
              <span className="font-mono text-[9px] text-white/40 uppercase tracking-widest">
                {user.role === "owner" ? "Dueño" : "Empleado"}
              </span>
            </button>
          )}
          <button
            onClick={changeStore}
            className="hidden md:flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/40 hover:text-white transition-colors"
            data-testid="dash-change-store"
            title="Cambiar tienda"
          >
            <StoreIcon size={14} /> <LogOut size={12} />
          </button>
        </div>
      </div>
      <PinSetupModal open={pinModalOpen} onClose={() => setPinModalOpen(false)} />
    </header>
  );
}
